import {
	IconPlusOutline18 as Plus,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type { ResolvedToolPolicyVariant } from "@productlane/lib"
import type { IconType } from "@productlane/ui/src/icons"
import { cn } from "@productlane/lib/src/cn"
import {
	IconButton,
	Tabs,
	TabsList,
	TabsTrigger,
	Tooltip,
} from "@productlane/ui"

interface Props {
	variants: readonly ResolvedToolPolicyVariant[]
	activeId: string
	onSelect: (id: string) => void
	onAdd: () => void
	onDelete: (id: string) => void
}

/** One chip per configuration of the tool, plus the way to add another. */
export function VariantTabs({
	variants,
	activeId,
	onSelect,
	onAdd,
	onDelete,
}: Props) {
	return (
		<div className="flex items-center gap-1.5">
			<Tabs
				value={activeId}
				onValueChange={onSelect}
				className="-ml-1 min-w-0 flex-1"
			>
				{/* Padded on every side: overflow-x clips both axes, so without it the
				    first chip's ring and each pill's shadow get shaved. */}
				<TabsList className="no-scrollbar w-auto justify-start overflow-x-auto p-1">
					{variants.map((variant) => (
						<TabsTrigger
							key={variant.id || "base"}
							value={variant.id}
							// TabsTrigger wraps its children in a plain span, whose default
							// min-width:auto would keep the label at max-content and spill it
							// out of the capped pill.
							className="max-w-48 flex-none overflow-hidden [&>span]:min-w-0"
							data-testid={`policy-variant-tab-${variant.id || "base"}`}
							onKeyDown={(event) => {
								if (!variant.id) return
								if (event.key !== "Delete" && event.key !== "Backspace") return
								event.preventDefault()
								onDelete(variant.id)
							}}
						>
							{/* One flex wrapper because TabsTrigger puts every child inside a
							    single inline span, where a second flex child would break onto
							    its own line. */}
							<span className="flex min-w-0 items-center gap-1">
								<span className="min-w-0 truncate">{variant.label}</span>
								{variant.id ? (
									// The slot stays mounted, so only opacity changes on hover and
									// the chip keeps its width.
									<ChipAction
										Icon={XIcon}
										label={`Delete ${variant.label}`}
										testId={`policy-variant-delete-${variant.id}`}
										onTrigger={() => onDelete(variant.id)}
										className="opacity-0 transition-opacity group-hover:opacity-100"
									/>
								) : null}
							</span>
						</TabsTrigger>
					))}
				</TabsList>
			</Tabs>
			<Tooltip label="Add another configuration of this tool">
				<IconButton
					variant="ghost"
					Icon={Plus}
					aria-label="Add a configuration"
					onClick={onAdd}
					data-testid="policy-variant-add"
				/>
			</Tooltip>
		</div>
	)
}

// A span rather than IconButton: the tab trigger is itself a button, and Radix
// activates it on pointer-down, so a nested button is invalid markup whose click
// never lands. Keyboard reaches these through the trigger's own handler.
function ChipAction({
	Icon,
	label,
	testId,
	onTrigger,
	className,
}: {
	Icon: IconType
	label: string
	testId: string
	onTrigger: () => void
	className?: string
}) {
	return (
		<span
			role="button"
			tabIndex={-1}
			aria-label={label}
			data-testid={testId}
			className={cn(
				"flex flex-none cursor-default items-center rounded p-0.5 text-tertiary hover:bg-gray-200 hover:text-primary dark:hover:bg-gray-700",
				className,
			)}
			onPointerDown={(event) => {
				event.preventDefault()
				event.stopPropagation()
			}}
			onClick={(event) => {
				event.preventDefault()
				event.stopPropagation()
				onTrigger()
			}}
			onKeyDown={(event) => {
				if (event.key !== "Enter" && event.key !== " ") return
				event.preventDefault()
				event.stopPropagation()
				onTrigger()
			}}
		>
			<Icon className="h-3 w-3 flex-none" />
		</span>
	)
}
